/**
 * Questionario IDD — Fondi Pensione (Reg. IVASS 40/2018 + POG Reale Mutua)
 *
 * Replica fedele del Questionario Previdenza Complementare ed. 04-2026 di
 * Reale Mutua. 14 domande in 4 sezioni:
 *   1. Profilo professionale
 *   2. Domicilio + Persona fisica
 *   3. Esigenze e bisogni (Demands & Needs)
 *   4. Conoscenza ed esperienza (Knowledge & Experience)
 *
 * Risultato: pre-classificazione di adeguatezza Classic vs Premium,
 * con disclaimer che la consulenza definitiva avviene in agenzia.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronRight, ChevronLeft, CheckCircle, AlertTriangle, XCircle,
  Briefcase, Users, GraduationCap, Globe, Heart, BookOpen, Shield,
  ArrowRight, FileCheck, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CodiceFiscaleField from "@/components/common/CodiceFiscaleField";
import ComuneAutocomplete from "@/components/common/ComuneAutocomplete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["Profilo lavorativo", "Domicilio", "Esigenze", "Conoscenza", "Risultato", "Contatti"];

export default function FondiPensioneQuestionarioPage() {
  const [params] = useSearchParams();
  const interesseInit = params.get("prodotto") || ""; // "classic" / "premium"
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [resultIdd, setResultIdd] = useState(null);
  const nav = useNavigate();

  const [idd, setIdd] = useState({
    eta: 35,                  // età obbligatoria (vincolo POG Premium 18-66)
    profilo_lavorativo: null, // dipendente | autonomo | titolare_attivita | casalinga | pensionato | studente | non_occupato
    sotto_categoria_lavoro: "",
    domicilio_see: null,
    persona_fisica: true,
    coperture_esistenti: [],  // nessuna|protezione|risparmio|previdenza
    contributo_min_50_eur: null,
    obiettivo: null,           // solo_pensione | pensione_e_protezione | non_lo_so
    desidera_morte_invalidita: null,
    conoscenza_finanziaria: null,
    conoscenza_prodotto: null,
    comprensione_post_spiegazione: null,
  });

  const [lead, setLead] = useState({
    nome: "", cognome: "", email: "", telefono: "",
    data_nascita: "", sesso: "", luogo_nascita: "", codice_fiscale: "", cap: "",
    interesse: interesseInit || "non_so",
    contributo_indicativo_mensile: 100,
    obiettivo_breve: "", note: "",
    privacy: false, info_precontrattuali_lette: false, marketing: false, profilazione: false,
  });

  // Calcola adeguatezza appena entriamo nello step 4 (Risultato)
  useEffect(() => {
    if (step === 4 && !resultIdd) {
      axios.post(`${API}/fondi-pensione/idd-questionnaire`, idd)
        .then(r => setResultIdd(r.data))
        .catch(() => toast.error("Errore nel calcolo. Torna indietro e riprova."));
    }
  }, [step, idd, resultIdd]);

  const next = () => {
    if (!validateStep(step, idd)) return;
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep(s => Math.max(s - 1, 0));

  const submit = async () => {
    if (submitting) return;
    if (!lead.privacy) { toast.error("Accetta la privacy policy"); return; }
    if (!lead.info_precontrattuali_lette) { toast.error("Conferma di aver preso visione dei documenti precontrattuali (CIR, Nota Informativa, KID)."); return; }
    if (!lead.nome || !lead.cognome || !lead.email || !lead.telefono) { toast.error("Compila i campi obbligatori"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/fondi-pensione/lead`, { ...lead, idd_result: resultIdd });
      toast.success("Richiesta inviata!");
      nav("/conferma");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore. Riprova.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="fondipensione-questionario-page">
      <Header />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="questionario-idd">
        <Link to="/fondi-pensione" className="text-xs text-[#4A5E51] hover:underline inline-flex items-center gap-1 mb-4">
          <ChevronLeft className="w-3 h-3" /> Torna alla pagina Fondi Pensione
        </Link>

        <div className="mb-3">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D97B58] font-semibold mb-1">Questionario IDD · Reg. IVASS 40/2018</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#0F2F1F]">Verifica adeguatezza prodotto</h1>
          <p className="text-sm text-[#4A5E51] mt-2">Rispondi a poche domande: in 4 minuti scopri se Pensione Reale Classic o Premium è adatto a te.</p>
        </div>

        {/* Stepper */}
        <div className="bg-white rounded-full p-1 mt-6 mb-6 border border-[#E5E2DB] flex" data-testid="stepper">
          {STEPS.map((s, i) => (
            <div key={s} className={`flex-1 text-center text-[10px] sm:text-xs font-semibold py-2 rounded-full transition-all ${i === step ? "bg-[#163321] text-white" : i < step ? "text-[#163321]" : "text-[#4A5E51]/50"}`}>
              <span className="hidden sm:inline">{i + 1}. {s}</span>
              <span className="sm:hidden">{i + 1}</span>
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="bg-white rounded-3xl p-5 sm:p-7 border border-[#E5E2DB]">
          {step === 0 && <StepProfilo idd={idd} setIdd={setIdd} />}
          {step === 1 && <StepDomicilio idd={idd} setIdd={setIdd} />}
          {step === 2 && <StepEsigenze idd={idd} setIdd={setIdd} />}
          {step === 3 && <StepConoscenza idd={idd} setIdd={setIdd} />}
          {step === 4 && <StepRisultato resultIdd={resultIdd} setLead={setLead} />}
          {step === 5 && <StepContatti lead={lead} setLead={setLead} />}

          <div className="flex justify-between mt-6 pt-5 border-t border-[#E5E2DB]">
            {step > 0 ? (
              <Button onClick={back} variant="ghost" className="text-[#4A5E51]" data-testid="btn-back"><ChevronLeft className="w-4 h-4 mr-1" /> Indietro</Button>
            ) : <span />}
            {step < STEPS.length - 1 ? (
              <Button onClick={next} className="bg-[#163321] hover:bg-[#0F2F1F] text-white rounded-full" data-testid="btn-next">
                {step === 4 ? "Procedi al contatto" : "Avanti"} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={submitting} className="bg-[#D97B58] hover:bg-[#B85A3D] text-white rounded-full" data-testid="btn-submit">
                {submitting ? "Invio…" : "Invia richiesta"}
              </Button>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

// ============================================================================
// VALIDAZIONE PER STEP
// ============================================================================
function validateStep(step, idd) {
  if (step === 0) {
    if (!idd.profilo_lavorativo) { toast.error("Seleziona la tua condizione professionale"); return false; }
    if (idd.eta === null || idd.eta === undefined || idd.eta < 0 || idd.eta > 99) { toast.error("Inserisci la tua età (anni compiuti)"); return false; }
  }
  if (step === 1) {
    if (idd.domicilio_see === null) { toast.error("Indica il tuo domicilio"); return false; }
    if (!idd.persona_fisica) { toast.error("Il prodotto è destinato a persone fisiche"); return false; }
  }
  if (step === 2) {
    if (idd.contributo_min_50_eur === null) { toast.error("Indica il contributo minimo previsto"); return false; }
    if (!idd.obiettivo) { toast.error("Indica l'obiettivo della copertura"); return false; }
  }
  if (step === 3) {
    if (!idd.conoscenza_finanziaria) { toast.error("Valuta la tua conoscenza degli strumenti finanziari"); return false; }
    if (!idd.conoscenza_prodotto) { toast.error("Valuta la tua conoscenza del prodotto"); return false; }
    if ((idd.conoscenza_prodotto === "base" || idd.conoscenza_prodotto === "non_sufficiente") && idd.comprensione_post_spiegazione === null) {
      toast.error("Indica se hai compreso il prodotto dopo le spiegazioni del consulente"); return false;
    }
  }
  return true;
}

// ============================================================================
// STEP 0 — Profilo lavorativo (Q2-Q5)
// ============================================================================
function StepProfilo({ idd, setIdd }) {
  const profili = [
    { id: "dipendente", label: "Lavoratore dipendente", Icon: Briefcase, sub: ["impiegato", "quadro", "dirigente", "insegnante", "commesso", "operaio"] },
    { id: "autonomo", label: "Lavoratore autonomo", Icon: Users, sub: ["libero professionista", "avvocato/magistrato", "commercialista", "medico", "artigiano", "commerciante", "altro"] },
    { id: "titolare_attivita", label: "Titolare di attività", Icon: Briefcase, sub: [] },
    { id: "casalinga", label: "Casalinga / Casalingo", Icon: Heart, sub: [] },
    { id: "pensionato", label: "Pensionato/a", Icon: Heart, sub: [] },
    { id: "studente", label: "Studente/Studentessa", Icon: GraduationCap, sub: [] },
    { id: "non_occupato", label: "Non occupato/a", Icon: Users, sub: [] },
  ];

  return (
    <div data-testid="step-profilo">
      <h2 className="font-heading text-xl text-[#0F2F1F] mb-1">Qual è la tua attuale condizione lavorativa?</h2>
      <p className="text-xs text-[#4A5E51] mb-5">Domande 2-5 del Questionario Reale Mutua.</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {profili.map(p => (
          <button key={p.id} onClick={() => setIdd(s => ({ ...s, profilo_lavorativo: p.id, sotto_categoria_lavoro: "" }))}
            className={`text-left p-3 rounded-xl border-2 transition ${idd.profilo_lavorativo === p.id ? "bg-[#163321] text-white border-[#163321]" : "bg-white border-[#E5E2DB] hover:border-[#163321]/50"}`}
            data-testid={`profilo-${p.id}`}>
            <p.Icon className="w-4 h-4 mb-1.5" />
            <p className="text-sm font-semibold">{p.label}</p>
          </button>
        ))}
      </div>
      {/* Sotto-categoria se autonomo o dipendente */}
      {(idd.profilo_lavorativo === "dipendente" || idd.profilo_lavorativo === "autonomo") && (
        <div className="mt-4">
          <Label className="text-xs">Specifica (facoltativo)</Label>
          <select value={idd.sotto_categoria_lavoro} onChange={e => setIdd(s => ({ ...s, sotto_categoria_lavoro: e.target.value }))}
            className="w-full h-10 border border-[#E5E2DB] rounded-md px-3 text-sm" data-testid="sotto-cat">
            <option value="">— Scegli —</option>
            {profili.find(p => p.id === idd.profilo_lavorativo).sub.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      )}

      {/* ETÀ — vincolo POG Premium (18-66) */}
      <div className="mt-5 pt-5 border-t border-[#E5E2DB]">
        <Label className="text-xs text-[#163321]/80 mb-1.5 block">Quanti anni hai? (anni compiuti)</Label>
        <Input
          type="number"
          min={0}
          max={99}
          value={idd.eta}
          onChange={e => setIdd(s => ({ ...s, eta: parseInt(e.target.value || "0") }))}
          className="h-11 text-base"
          data-testid="eta-input"
        />
        {idd.eta > 66 && (
          <p className="text-[11px] text-amber-700 mt-1.5" data-testid="warn-eta">
            ⚠ Pensione Reale Premium prevede sottoscrizione fino a 66 anni. Pensione Reale Classic non ha limiti di età.
          </p>
        )}
        {idd.eta && idd.eta < 18 && (
          <p className="text-[11px] text-amber-700 mt-1.5">
            ⚠ Pensione Reale Premium è riservata ai maggiorenni. Pensione Reale Classic non ha limiti di età minima ed è ideale anche per i minori (vantaggi fiscali a lungo termine).
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// STEP 1 — Domicilio + Persona fisica (Q6-Q7)
// ============================================================================
function StepDomicilio({ idd, setIdd }) {
  return (
    <div data-testid="step-domicilio">
      <h2 className="font-heading text-xl text-[#0F2F1F] mb-1">Dove vivi?</h2>
      <p className="text-xs text-[#4A5E51] mb-5">Domanda 6 del Questionario.</p>

      <div className="grid sm:grid-cols-2 gap-2 mb-5">
        <Choice active={idd.domicilio_see === true} onClick={() => setIdd(s => ({ ...s, domicilio_see: true }))} title="🇪🇺 Italia o SEE" subtitle="Spazio Economico Europeo" testid="dom-see" />
        <Choice active={idd.domicilio_see === false} onClick={() => setIdd(s => ({ ...s, domicilio_see: false }))} title="🌍 Resto del mondo" subtitle="Fuori SEE" testid="dom-rest" />
      </div>

      {idd.domicilio_see === false && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-800 mb-4" data-testid="warn-fuori-see">
          <AlertTriangle className="w-4 h-4 inline mr-1" /> Il prodotto è destinato esclusivamente a soggetti residenti nello Spazio Economico Europeo. Contatta comunque un consulente per valutare alternative.
        </div>
      )}

      <p className="text-xs text-[#4A5E51] mt-6 mb-2">Confermi di sottoscrivere come persona fisica?</p>
      <div className="grid grid-cols-2 gap-2">
        <Choice active={idd.persona_fisica === true} onClick={() => setIdd(s => ({ ...s, persona_fisica: true }))} title="Sì" testid="pf-si" />
        <Choice active={idd.persona_fisica === false} onClick={() => setIdd(s => ({ ...s, persona_fisica: false }))} title="No (società/ente)" testid="pf-no" />
      </div>
    </div>
  );
}

// ============================================================================
// STEP 2 — Esigenze e bisogni (Q8-Q11)
// ============================================================================
function StepEsigenze({ idd, setIdd }) {
  const toggleCop = (val) => {
    setIdd(s => {
      const has = s.coperture_esistenti.includes(val);
      const next = has ? s.coperture_esistenti.filter(x => x !== val) : [...s.coperture_esistenti.filter(x => x !== "nessuna"), val];
      return { ...s, coperture_esistenti: val === "nessuna" ? ["nessuna"] : next };
    });
  };
  return (
    <div data-testid="step-esigenze">
      <h2 className="font-heading text-xl text-[#0F2F1F] mb-1">Le tue esigenze previdenziali</h2>
      <p className="text-xs text-[#4A5E51] mb-5">Domande 8-11 del Questionario.</p>

      {/* Q8 — Coperture esistenti */}
      <div className="mb-5">
        <p className="text-sm font-semibold text-[#163321] mb-2">Hai già coperture assicurative attive?</p>
        <p className="text-[11px] text-[#4A5E51] mb-2">(puoi selezionare più voci)</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {[
            { val: "nessuna", label: "Nessuna copertura" },
            { val: "protezione", label: "Protezione (rischio morte, malattie gravi, non autosufficienza)" },
            { val: "risparmio", label: "Risparmio / investimento" },
            { val: "previdenza", label: "Tutela previdenziale (PIP, fondo pensione, rendita)" },
          ].map(c => (
            <label key={c.val} className={`flex items-start gap-2 p-3 rounded-xl border cursor-pointer ${idd.coperture_esistenti.includes(c.val) ? "bg-[#163321]/5 border-[#163321]" : "border-[#E5E2DB]"}`}>
              <Checkbox checked={idd.coperture_esistenti.includes(c.val)} onCheckedChange={() => toggleCop(c.val)} data-testid={`cop-${c.val}`} />
              <span className="text-xs text-[#163321]">{c.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Q9 — Contributo minimo 50€ */}
      <div className="mb-5">
        <p className="text-sm font-semibold text-[#163321] mb-1">Prevedi di versare almeno € 50 all'anno di contributo volontario?</p>
        <p className="text-[11px] text-[#4A5E51] mb-2">Necessario per attivare la garanzia complementare obbligatoria Long Term Care del Premium.</p>
        <div className="grid grid-cols-2 gap-2">
          <Choice active={idd.contributo_min_50_eur === true} onClick={() => setIdd(s => ({ ...s, contributo_min_50_eur: true }))} title="Sì" testid="contr-si" />
          <Choice active={idd.contributo_min_50_eur === false} onClick={() => setIdd(s => ({ ...s, contributo_min_50_eur: false }))} title="No / non so" testid="contr-no" />
        </div>
      </div>

      {/* Q10 — Obiettivo (3 opzioni equilibrate, senza bias verso Premium) */}
      <div className="mb-5">
        <p className="text-sm font-semibold text-[#163321] mb-1">Qual è il tuo obiettivo principale?</p>
        <p className="text-[11px] text-[#4A5E51] mb-2">Rispondi onestamente: la scelta determina quale prodotto fa per te. Non c'è un'opzione "migliore".</p>
        <div className="space-y-2">
          <Choice
            active={idd.obiettivo === "solo_pensione"}
            onClick={() => setIdd(s => ({ ...s, obiettivo: "solo_pensione", desidera_morte_invalidita: null }))}
            title="🎯 Solo pensione integrativa"
            subtitle="Mi serve un'integrazione della rendita INPS a fine carriera. Niente coperture aggiuntive (LTC, morte, invalidità) — voglio massimizzare la rendita pensionistica."
            testid="obj-solo"
          />
          <Choice
            active={idd.obiettivo === "pensione_e_protezione"}
            onClick={() => setIdd(s => ({ ...s, obiettivo: "pensione_e_protezione" }))}
            title="🛡 Pensione + protezione contro perdita autosufficienza"
            subtitle="Oltre all'integrazione pensionistica, voglio anche una rendita vitalizia se diventassi non autosufficiente (Long Term Care). Sono disposto a destinare parte del contributo a questa garanzia."
            testid="obj-protezione"
          />
          <Choice
            active={idd.obiettivo === "non_lo_so"}
            onClick={() => setIdd(s => ({ ...s, obiettivo: "non_lo_so", desidera_morte_invalidita: null }))}
            title="🤝 Non sono sicuro — aiutatemi voi"
            subtitle="Voglio capire prima quale opzione conviene di più al mio caso. Un consulente Colucci mi guiderà."
            testid="obj-non-so"
          />
        </div>
      </div>

      {/* Q11 — Desidera Morte/Invalidità (solo se obiettivo include protezione) */}
      {idd.obiettivo === "pensione_e_protezione" && idd.contributo_min_50_eur && (
        <div className="bg-[#FFF6EE] border border-[#E8D3A0] rounded-xl p-3">
          <p className="text-sm font-semibold text-[#7A4A2E] mb-1">Oltre alla LTC obbligatoria, desideri anche la garanzia Morte/Invalidità Permanente facoltativa?</p>
          <p className="text-[10px] text-[#7A4A2E]/80 mb-2">Costo: € 60 annui (capitale assicurato decrescente in base all'età).</p>
          <div className="grid grid-cols-2 gap-2">
            <Choice active={idd.desidera_morte_invalidita === true} onClick={() => setIdd(s => ({ ...s, desidera_morte_invalidita: true }))} title="Sì" testid="mi-si" />
            <Choice active={idd.desidera_morte_invalidita === false} onClick={() => setIdd(s => ({ ...s, desidera_morte_invalidita: false }))} title="No" testid="mi-no" />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// STEP 3 — Conoscenza ed esperienza (Q12-Q14)
// ============================================================================
function StepConoscenza({ idd, setIdd }) {
  return (
    <div data-testid="step-conoscenza">
      <h2 className="font-heading text-xl text-[#0F2F1F] mb-1">Conoscenza ed esperienza</h2>
      <p className="text-xs text-[#4A5E51] mb-5">Domande 12-14 del Questionario.</p>

      {/* Q12 — Conoscenza strumenti finanziari */}
      <div className="mb-5">
        <p className="text-sm font-semibold text-[#163321] mb-2">Come valuti la tua conoscenza degli strumenti finanziari?</p>
        <p className="text-[11px] text-[#4A5E51] mb-2">(Azioni, Fondi, Titoli di Stato, Obbligazioni, Derivati)</p>
        <div className="space-y-2">
          {[
            { id: "alta", label: "Alta", desc: "Conoscenza approfondita di tutti gli strumenti finanziari" },
            { id: "medio_alta", label: "Medio-Alta", desc: "Conoscenza di base di tutti gli strumenti finanziari" },
            { id: "medio_bassa", label: "Medio-Bassa", desc: "Conoscenza degli strumenti meno complessi (Fondi, Titoli, Obbligazioni)" },
            { id: "bassa", label: "Bassa", desc: "Nessuna o limitata conoscenza degli strumenti finanziari" },
          ].map(c => (
            <Choice key={c.id} active={idd.conoscenza_finanziaria === c.id} onClick={() => setIdd(s => ({ ...s, conoscenza_finanziaria: c.id }))} title={c.label} subtitle={c.desc} testid={`cf-${c.id}`} />
          ))}
        </div>
      </div>

      {/* Q13 — Conoscenza prodotto */}
      <div className="mb-5">
        <p className="text-sm font-semibold text-[#163321] mb-2">Come valuti la tua conoscenza del funzionamento di un PIP?</p>
        <p className="text-[11px] text-[#4A5E51] mb-2">(es. disponibilità del capitale solo al pensionamento o al verificarsi di eventi previsti dalla legge)</p>
        <div className="space-y-2">
          {[
            { id: "avanzata", label: "Avanzata", desc: "Conoscenza avanzata di copertura, garanzie, limiti ed esclusioni del prodotto" },
            { id: "buona", label: "Buona", desc: "Conoscenza buona dell'ambito di copertura del prodotto" },
            { id: "base", label: "Di base", desc: "Conoscenza di base — necessita di approfondimento" },
            { id: "non_sufficiente", label: "Non sufficiente", desc: "Nessuna o limitata conoscenza del prodotto" },
          ].map(c => (
            <Choice key={c.id} active={idd.conoscenza_prodotto === c.id} onClick={() => setIdd(s => ({ ...s, conoscenza_prodotto: c.id, comprensione_post_spiegazione: null }))} title={c.label} subtitle={c.desc} testid={`cp-${c.id}`} />
          ))}
        </div>
      </div>

      {/* Q14 — Comprensione post spiegazione (solo se prodotto base/non_sufficiente) */}
      {(idd.conoscenza_prodotto === "base" || idd.conoscenza_prodotto === "non_sufficiente") && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="q14-block">
          <p className="text-sm font-semibold text-amber-900 mb-1">Confermi di aver compreso la copertura, le garanzie e i limiti del prodotto dopo aver letto i documenti precontrattuali (CIR, Nota Informativa)?</p>
          <p className="text-[10px] text-amber-800 mb-2">Domanda 14 del Questionario IDD: necessaria quando la conoscenza del prodotto è di base o non sufficiente.</p>
          <div className="grid grid-cols-2 gap-2">
            <Choice active={idd.comprensione_post_spiegazione === true} onClick={() => setIdd(s => ({ ...s, comprensione_post_spiegazione: true }))} title="Sì, ho compreso" testid="cps-si" />
            <Choice active={idd.comprensione_post_spiegazione === false} onClick={() => setIdd(s => ({ ...s, comprensione_post_spiegazione: false }))} title="No, mi serve consulenza" testid="cps-no" />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// STEP 4 — Risultato adeguatezza
// ============================================================================
function StepRisultato({ resultIdd, setLead }) {
  if (!resultIdd) return <p className="text-center py-10 text-[#4A5E51]">Calcolo adeguatezza…</p>;

  const verdictMap = {
    adeguato: { Icon: CheckCircle, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-300", label: "Adeguato" },
    adeguato_con_avvertenze: { Icon: AlertTriangle, color: "text-amber-700", bg: "bg-amber-50 border-amber-300", label: "Adeguato con avvertenze" },
    non_adeguato: { Icon: XCircle, color: "text-red-700", bg: "bg-red-50 border-red-300", label: "Non adeguato" },
  };

  return (
    <div data-testid="step-risultato">
      <h2 className="font-heading text-xl text-[#0F2F1F] mb-2">Pre-classificazione di adeguatezza</h2>
      <p className="text-xs text-[#4A5E51] mb-5">Risultato in base ai POG dei prodotti Reale Mutua e al Reg. IVASS 40/2018.</p>

      {/* Verdetto per prodotto */}
      <div className="space-y-3 mb-4">
        {["classic", "premium"].map(slug => {
          const r = resultIdd[slug];
          const v = verdictMap[r.verdict] || verdictMap.adeguato;
          const isConsigliato = resultIdd.consigliato === slug;
          // Ring color: verde scuro per Classic, corallo per Premium — coerente col prodotto
          const ringColor = slug === "classic" ? "ring-[#163321]" : "ring-[#D97B58]";
          return (
            <div key={slug} className={`rounded-2xl p-4 border-2 ${v.bg} ${isConsigliato ? `ring-2 ${ringColor} ring-offset-2` : ""}`} data-testid={`verdict-${slug}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-heading text-lg text-[#0F2F1F]">{r.prodotto}</p>
                  {isConsigliato && <span className={`inline-block mt-0.5 text-[10px] uppercase font-bold tracking-wider ${slug === 'classic' ? 'text-[#163321]' : 'text-[#D97B58]'}`}>⭐ Consigliato per te</span>}
                </div>
                <div className={`flex items-center gap-1.5 ${v.color}`}>
                  <v.Icon className="w-5 h-5" />
                  <span className="text-xs font-bold uppercase">{v.label}</span>
                </div>
              </div>
              {r.warnings?.length > 0 && (
                <ul className="text-[11px] mt-2 space-y-1 text-amber-800 bg-white/60 rounded p-2">
                  {r.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}
                </ul>
              )}
              {r.reasons_no?.length > 0 && (
                <ul className="text-[11px] mt-2 space-y-1 text-red-800 bg-white/60 rounded p-2">
                  {r.reasons_no.map((w, i) => <li key={i}>✕ {w}</li>)}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {/* Box "consulenza necessaria" se nessun consigliato */}
      {!resultIdd.consigliato && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-900 mb-3" data-testid="consulenza-box">
          <Info className="w-4 h-4 inline mr-1" />
          <strong>Serve una consulenza personalizzata.</strong> {resultIdd.nota_consulenza || "Sulla base delle tue risposte non emerge un prodotto chiaramente adeguato in modalità self-service. Procedi al passo successivo per essere ricontattato dal consulente Colucci."}
        </div>
      )}

      {/* Disclaimer */}
      <div className="bg-[#FFF6EE] border border-[#E8D3A0] rounded-xl p-3 text-[11px] text-[#7A4A2E]">
        <Info className="w-4 h-4 inline mr-1" />
        {resultIdd.disclaimer}
      </div>

      <p className="text-xs text-[#4A5E51] mt-5">
        Procedi al passo successivo per essere ricontattato da un consulente Colucci e ricevere la quotazione personalizzata. Se hai dubbi, segnala "non sono sicuro" — il consulente ti aiuterà a scegliere.
      </p>

      {/* Pre-seleziona interesse in base al consigliato */}
      <SetInteresseHook resultIdd={resultIdd} setLead={setLead} />
    </div>
  );
}

function SetInteresseHook({ resultIdd, setLead }) {
  useEffect(() => {
    if (resultIdd?.consigliato) {
      setLead(s => ({ ...s, interesse: resultIdd.consigliato }));
    }
  }, [resultIdd, setLead]);
  return null;
}

// ============================================================================
// STEP 5 — Contatti + Consensi
// ============================================================================
function StepContatti({ lead, setLead }) {
  return (
    <div data-testid="step-contatti">
      <h2 className="font-heading text-xl text-[#0F2F1F] mb-1">I tuoi contatti</h2>
      <p className="text-xs text-[#4A5E51] mb-5">Inseriremo questi dati nel CRM Colucci. Ti chiameremo entro 24h.</p>

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Nome *"><Input value={lead.nome} onChange={e => setLead(s => ({ ...s, nome: e.target.value }))} data-testid="lead-nome" /></Field>
        <Field label="Cognome *"><Input value={lead.cognome} onChange={e => setLead(s => ({ ...s, cognome: e.target.value }))} data-testid="lead-cognome" /></Field>
        <Field label="Email *"><Input type="email" value={lead.email} onChange={e => setLead(s => ({ ...s, email: e.target.value }))} data-testid="lead-email" /></Field>
        <Field label="Telefono *"><Input value={lead.telefono} onChange={e => setLead(s => ({ ...s, telefono: e.target.value }))} data-testid="lead-tel" /></Field>
        <Field label="Data di nascita"><Input type="date" value={lead.data_nascita} onChange={e => setLead(s => ({ ...s, data_nascita: e.target.value }))} data-testid="lead-data" /></Field>
        <Field label="Sesso">
          <select value={lead.sesso} onChange={e => setLead(s => ({ ...s, sesso: e.target.value }))} className="w-full h-10 border border-[#E5E2DB] rounded-md px-3 text-sm" data-testid="lead-sesso">
            <option value="">—</option>
            <option value="M">Maschio</option>
            <option value="F">Femmina</option>
          </select>
        </Field>
        <Field label="Luogo di nascita">
          <ComuneAutocomplete
            value={lead.luogo_nascita}
            onChange={v => setLead(s => ({ ...s, luogo_nascita: v }))}
            placeholder="Es. Napoli"
          />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Codice fiscale (calcolato automaticamente)">
            <CodiceFiscaleField
              value={lead.codice_fiscale}
              onChange={v => setLead(s => ({ ...s, codice_fiscale: v }))}
              cognome={lead.cognome}
              nome={lead.nome}
              dataNascita={lead.data_nascita}
              sesso={lead.sesso}
              luogoNascita={lead.luogo_nascita}
            />
          </Field>
        </div>
        <Field label="CAP"><Input value={lead.cap} onChange={e => setLead(s => ({ ...s, cap: e.target.value.replace(/\D/g, "").slice(0, 5) }))} data-testid="lead-cap" /></Field>
      </div>

      <div className="mt-3">
        <Field label="Prodotto di interesse">
          <select value={lead.interesse} onChange={e => setLead(s => ({ ...s, interesse: e.target.value }))} className="w-full h-10 border border-[#E5E2DB] rounded-md px-3 text-sm" data-testid="lead-interesse">
            <option value="classic">Pensione Reale Classic</option>
            <option value="premium">Pensione Reale Premium</option>
            <option value="non_so">Non sono sicuro — aiutatemi voi</option>
            <option value="consulenza_generale">Consulenza generale previdenziale</option>
          </select>
        </Field>
      </div>

      <div className="mt-3">
        <Field label="Quanto vorresti versare al mese (indicativo, modificabile in agenzia)?">
          <Input type="number" value={lead.contributo_indicativo_mensile} onChange={e => setLead(s => ({ ...s, contributo_indicativo_mensile: parseFloat(e.target.value || "0") }))} data-testid="lead-contrib" />
        </Field>
      </div>

      <div className="mt-3">
        <Field label="Note (facoltative)">
          <Textarea rows={3} value={lead.note} onChange={e => setLead(s => ({ ...s, note: e.target.value }))} placeholder="Es. preferisco essere richiamato la mattina, ho già un altro PIP, ecc." data-testid="lead-note" />
        </Field>
      </div>

      {/* Consensi GDPR + IVASS 40/2018 */}
      <div className="mt-5 pt-5 border-t border-[#E5E2DB] space-y-2">
        <p className="text-xs font-semibold text-[#163321] mb-2">Consensi obbligatori</p>
        <ConsentRow
          checked={lead.info_precontrattuali_lette}
          onChange={v => setLead(s => ({ ...s, info_precontrattuali_lette: v }))}
          required
          testid="cons-info"
        >
          Confermo di aver preso visione (o di voler ricevere prima della sottoscrizione) dei <strong>documenti precontrattuali</strong>: <Link to="/api/fondi-pensione/documents/common/cir" target="_blank" className="underline">CIR</Link>, Nota Informativa, Regolamento del Fondo, Documento Anticipazioni, Documento Rendite, Regime Fiscale.
        </ConsentRow>
        <ConsentRow
          checked={lead.privacy}
          onChange={v => setLead(s => ({ ...s, privacy: v }))}
          required
          testid="cons-privacy"
        >
          Acconsento al <strong>trattamento dei dati personali</strong> per la gestione della richiesta di consulenza, ai sensi del Reg. UE 679/2016 (GDPR) e del Reg. IVASS 40/2018 (informativa <Link to="/privacy" target="_blank" className="underline">qui</Link>).
        </ConsentRow>
        <ConsentRow
          checked={lead.marketing}
          onChange={v => setLead(s => ({ ...s, marketing: v }))}
          testid="cons-mkt"
        >
          Acconsento a ricevere comunicazioni promozionali e offerte commerciali da Colucci Assicuratori (facoltativo).
        </ConsentRow>
        <ConsentRow
          checked={lead.profilazione}
          onChange={v => setLead(s => ({ ...s, profilazione: v }))}
          testid="cons-prof"
        >
          Acconsento alla <strong>profilazione</strong> dei miei dati per ricevere proposte personalizzate (facoltativo).
        </ConsentRow>
      </div>
    </div>
  );
}

// ============================================================================
// HELPERS UI
// ============================================================================
function Field({ label, children }) { return (<div><Label className="text-xs text-[#163321]/70 mb-1 block">{label}</Label>{children}</div>); }

function Choice({ active, onClick, title, subtitle, testid }) {
  return (
    <button onClick={onClick} className={`text-left p-3 rounded-xl border-2 transition w-full ${active ? "bg-[#163321] text-white border-[#163321]" : "bg-white border-[#E5E2DB] hover:border-[#163321]/50"}`} data-testid={testid}>
      <p className="text-sm font-semibold">{title}</p>
      {subtitle && <p className={`text-[11px] mt-0.5 ${active ? "text-white/70" : "text-[#4A5E51]"}`}>{subtitle}</p>}
    </button>
  );
}

function ConsentRow({ checked, onChange, required, testid, children }) {
  return (
    <label className="flex items-start gap-2 p-2 rounded-lg hover:bg-[#F5F4F0] cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={v => onChange(!!v)} className="mt-0.5" data-testid={testid} />
      <span className="text-[11px] text-[#4A5E51] leading-relaxed flex-1">
        {children} {required && <span className="text-red-600 font-bold">*</span>}
      </span>
    </label>
  );
}
